/**
 * signerAudience — which field audiences THIS session is allowed to answer.
 *
 * The single client-side source of truth for the patient/plan-holder split, so
 * the questionnaire (bridgeQuestionnaire) and the sign gate (signReadiness)
 * can never disagree about who owns a question.
 *
 * WHERE THE SPA LEARNS THE SEND SHAPE. The bridge stamps a `role` on the
 * redeemed session (BridgeLanding stores `payload.role || "legacy"`):
 *
 *   "patient"      split-link send, the PATIENT's leg. The plan member is a
 *                  DIFFERENT person and has their own magic link, so this
 *                  session answers `patient` fields only.
 *   "plan_member"  split-link send, the PLAN MEMBER's leg — `plan_holder` only.
 *   "legacy"       SINGLE-SIGNER send. The Mac mints this link with
 *                  `signer_role=None` (routers/bridge.py) and the bridge maps a
 *                  null signer_role to role "legacy" (bridge/routers/magic.py),
 *                  which happens exactly when `use_split_link` is false — the
 *                  plan-member relationship is 'self' or blank, the physician
 *                  deferred the plan-member relay, or the form has no
 *                  plan-member signature at all. One person answers for BOTH
 *                  parties, so this session owns `patient` AND `plan_holder`.
 *
 * Matching one audience by equality is what silently dropped a form's
 * contract-number / member-ID / plan-sponsor asks from a single-signer link
 * once its plan-member block was tagged `plan_holder` (272 Sun Life XFA fields,
 * the corpus convention). Mirrors `patient_schema_audiences` in
 * med_form_app/backend/routers/bridge.py — keep the two in lockstep.
 */

export const PATIENT_AUDIENCE = "patient";
export const PLAN_HOLDER_AUDIENCE = "plan_holder";

/** Audiences the given session role may answer for. */
export function signerAudiencesFor(role: string | null | undefined): string[] {
  if (role === "plan_member") return [PLAN_HOLDER_AUDIENCE];
  if (role === "patient") return [PATIENT_AUDIENCE];
  // "legacy", or a session predating the role stamp: single-signer.
  return [PATIENT_AUDIENCE, PLAN_HOLDER_AUDIENCE];
}

/**
 * Normalize an audience option (one, several, or none) to a membership test.
 * `null` means "no audience filter" — every field passes.
 */
export function audienceMatcher(
  audience: string | string[] | null | undefined
): ((fieldAudience: string | null | undefined) => boolean) | null {
  if (!audience) return null;
  const wanted = new Set(
    (Array.isArray(audience) ? audience : [audience]).filter(Boolean)
  );
  if (wanted.size === 0) return null;
  // An UNTAGGED field always passes — back-compat with forms that predate the
  // question-model, same rule as the backend builder.
  return (fieldAudience) => !fieldAudience || wanted.has(String(fieldAudience));
}

export default signerAudiencesFor;
